import { MembershipBillingPlan, ProductFeature } from "@app/shared/types";
import { alpha, Button, Typography } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { analyticsTrack } from "app/analytics/track";
import LoadingBar from "components/LoadingBar";
import { PricingCardBillingOptions } from "features/billing/PricingCardBillingOptions";
import {
    getCurrentCohort,
    getSignupAnalyticsMetadataFromPlan,
    isUserInFreeTrialSession,
    isUserOutsideOfMemberZone,
} from "features/signup/signupHelpers";
import { useEffect } from "react";
import CheckIcon from "@mui/icons-material/Check";
import PageWrapper from "components/PageWrapper";
import { useSelector } from "react-redux";
import { selectUser } from "features/auth/auth";
import { SignupProgressBar } from "./SignupProgressBar";
import { FixedCTAContainer } from "./FixedCTAContainer";
import classNames from "classnames";
import { useLocation } from "react-router";

const useStyles = makeStyles((theme) => ({
    contentContainer: {
        paddingBottom: theme.spacing(18),
        [theme.breakpoints.down("sm")]: {
            paddingBottom: theme.spacing(12),
        },
    },
    membershipContainer: {
        display: "flex",
        flexDirection: "row",
        gap: theme.spacing(6),
        [theme.breakpoints.down("lg")]: {
            flexDirection: "column",
            gap: theme.spacing(2),
        },
    },
    cohortMembershipContainer: {
        display: "flex",
        flexDirection: "column",
        gap: theme.spacing(2),
    },
    checkIcon: {
        color: theme.palette.grey700,
        backgroundColor: "#E4DDD5",
        borderRadius: theme.borderRadius.circular,
        padding: theme.spacing(0.5),
        marginRight: theme.spacing(1),
        verticalAlign: "text-top",
    },
    membershipDetails: {
        minWidth: 300,
        [theme.breakpoints.down("lg")]: {
            marginBottom: theme.spacing(2),
        },
    },
    membershipDetailsBulletPoints: {
        [theme.breakpoints.down("lg")]: {
            marginBottom: theme.spacing(1),
        },
    },
    contributionInfo: {
        backgroundColor: alpha(theme.palette.grey700, 0.09),
        padding: theme.spacing(3),
        borderRadius: theme.borderRadius.default,
    },
    pageWrapper: {
        position: "relative",
        marginTop: 0,
    },
    subtitle: {
        marginTop: theme.spacing(-2),
        marginBottom: theme.spacing(3),
    },
}));

export interface SlidingScalePricingStepProps {
    billingPlanOptions: MembershipBillingPlan[];
    billingPlan: MembershipBillingPlan | undefined;
    onChangeBillingPlan: (plan: MembershipBillingPlan) => void;
    onContinue: () => void;
    customTitleText?: string;
    note?: React.ReactNode;
    continueButtonText?: string;
}

const getTitle = (isCohortSponsored: boolean) => {
    if (isCohortSponsored) {
        return "Your membership";
    } else {
        return "Choose your rate";
    }
};

const getSubtitle = (isFreeTrialSignupFlow: boolean, cohort: string | null) => {
    if (isFreeTrialSignupFlow) {
        return "Activate your free trial by choosing the monthly rate you'd pay if you choose to stay on after your trial. You can change it later.";
    } else if (cohort) {
        return null;
    } else {
        return "Choose your rate on the sliding scale. You can change it later.";
    }
};

export const SignupBillingPricePage = (props: SlidingScalePricingStepProps) => {
    const {
        billingPlan,
        onChangeBillingPlan,
        billingPlanOptions,
        onContinue,
        customTitleText,
        note,
        continueButtonText,
    } = props;

    const classes = useStyles();
    const user = useSelector(selectUser);
    const isActiveMember = user?.features?.includes(ProductFeature.CORE_MEMBERSHIP);

    const location = useLocation();
    const isFreeTrialSignupFlow = isUserInFreeTrialSession() && isUserOutsideOfMemberZone(location);

    const cohort = getCurrentCohort();
    const isCohortSponsored = cohort !== null;

    // Select the middle option if none is selected yet
    useEffect(() => {
        if (!billingPlan) {
            const defaultPlan = billingPlanOptions[Math.floor((billingPlanOptions.length - 1) / 2)];
            if (defaultPlan) {
                onChangeBillingPlan(defaultPlan);
            }
        }
    }, [billingPlan]);

    if (!billingPlan) {
        return <LoadingBar />;
    }

    const title = getTitle(isCohortSponsored);
    const subtitle = getSubtitle(isFreeTrialSignupFlow, cohort);

    return (
        <>
            {!isActiveMember && <SignupProgressBar currentStep={0} />}
            <PageWrapper className={classes.pageWrapper}>
                <div className={classes.contentContainer}>
                    <Typography variant="h1">{customTitleText || title}</Typography>
                    <Typography variant="body1" className={classes.subtitle}>
                        {subtitle}
                    </Typography>
                    <div
                        className={classNames(classes.membershipContainer, {
                            [classes.cohortMembershipContainer]: isCohortSponsored,
                        })}
                    >
                        <PricingCardBillingOptions
                            billingPlanOptions={billingPlanOptions}
                            value={billingPlan}
                            onChange={(plan) => onChangeBillingPlan(plan)}
                        />

                        <div className={classes.membershipDetails}>
                            <Typography
                                variant="body1"
                                className={classes.membershipDetailsBulletPoints}
                            >
                                <strong>Your membership includes:</strong>
                            </Typography>
                            <Typography
                                variant="body1"
                                className={classes.membershipDetailsBulletPoints}
                            >
                                <CheckIcon className={classes.checkIcon} />
                                60+ weekly groups on 15+ different topics
                            </Typography>
                            <Typography
                                variant="body1"
                                className={classes.membershipDetailsBulletPoints}
                            >
                                <CheckIcon className={classes.checkIcon} />
                                8+ live talks per month
                            </Typography>
                            <Typography
                                variant="body1"
                                className={classes.membershipDetailsBulletPoints}
                            >
                                <CheckIcon className={classes.checkIcon} />
                                140+ live meditations per month
                            </Typography>
                            <Typography
                                variant="body1"
                                className={classes.membershipDetailsBulletPoints}
                            >
                                <CheckIcon className={classes.checkIcon} />
                                60+ highly qualified teachers
                            </Typography>
                        </div>
                    </div>

                    {!isCohortSponsored && (
                        <div className={classes.contributionInfo}>
                            <Typography variant="h4" data-testid="contributionTitle">
                                How is my contribution used?
                            </Typography>
                            <Typography variant="body1" sx={{ mb: 0 }}>
                                We are a public benefit company. We{" "}
                                <strong>compensate our teachers fairly</strong>, provide
                                <strong> financial support to members</strong> with fewer means or
                                from marginalised communities and we <strong>pay our team</strong>{" "}
                                and <strong>invest in our technology</strong>.
                            </Typography>
                        </div>
                    )}
                </div>

                <FixedCTAContainer>
                    <Button
                        variant="primary"
                        data-testid="continueButton"
                        onClick={() => {
                            const analyticsData = getSignupAnalyticsMetadataFromPlan({
                                plan: billingPlan,
                                donationAmount: 0,
                            });
                            analyticsTrack("slidingScale.submit", {
                                ...analyticsData,
                                isFreeTrial: isFreeTrialSignupFlow,
                                cohort: cohort,
                            });
                            onContinue();
                        }}
                    >
                        Continue
                    </Button>
                </FixedCTAContainer>
            </PageWrapper>
        </>
    );
};
